import { type Customer } from '@hungryroot/customer';
import { Sweets2 } from '@hungryroot/icons';
import { FoodProfileDisclosure } from '@hungryroot/page-food-profile';
import { Paragraph, Heading } from '@hungryroot/ramen';
import {
  tagGroups,
  useCustomerTagGroup,
  type CustomerTagGroupResponse,
} from '@hungryroot/tags';

import {
  useTagGroup,
  type Tag,
} from '../../../../api/v2/tag_groups/get/v2-tag-groups-get';
import {
  ControlTagTernary,
  TagPreference,
} from '../ControlTagTernary/ControlTagTernary';
import styles from './SweetsSection.module.css';

interface Props {
  testid?: string;
  customerId: Customer['id'];
}

export function getSubtitle(
  queryTags: Tag[] | undefined,
  customerTags: CustomerTagGroupResponse | undefined
) {
  if (!customerTags || !queryTags) {
    return;
  }

  const selectedTagDetails = queryTags.reduce((acc: Tag[], cur: Tag) => {
    return customerTags.results.some(
      (obj) =>
        obj.tag === cur.id &&
        (obj.preference === TagPreference.Absolutely ||
          obj.preference === TagPreference.SoSo)
    )
      ? [...acc, cur]
      : [...acc];
  }, []);
  const tagNames = selectedTagDetails?.map((tag) => tag.name);

  return tagNames?.join(', ');
}

export function SweetsSection(props: Props) {
  const { testid, customerId } = props;

  const tagGroup = useTagGroup(tagGroups.sweets);
  const { data: tagGroupData } = tagGroup || {};
  const queryTags = tagGroupData?.tags;
  const { data: customerTagGroupData } = useCustomerTagGroup(tagGroups.sweets);
  const subtitle = getSubtitle(queryTags, customerTagGroupData);

  return (
    <section data-testid={testid} className={styles.container}>
      <FoodProfileDisclosure
        testid={`${testid}-disclosure`}
        title="Sweets"
        subtitle={subtitle || 'Set your preferences.'}
        icon={<Sweets2 />}
        deepLink="sweets-categories"
      >
        <Heading className={styles.title} variant="H5">
          Do you like these kinds of sweets?
        </Heading>
        <Paragraph className={styles.text}>
          We’ll use your preferences to suggest more items that suit your
          tastes.
        </Paragraph>
        <ControlTagTernary
          testid={`${testid}-control`}
          customerId={customerId}
          tagGroupId={tagGroups.sweets}
        />
      </FoodProfileDisclosure>
    </section>
  );
}
